/**
 * VoxelEngine - Orchestrates rendering and input handling
 */

import { Block } from "./types";
import { BlockRenderer } from "./renderer/base";
import { WebGLBlockRenderer } from "./renderer/webgl";
import { CSSBlockRenderer } from "./renderer/css";
import { BlockModel } from "./model";

type RendererType = "webgl" | "css" | "auto";

/**
 * Parse renderer type from URL query parameter
 * @returns Renderer type from URL or 'auto' if not specified
 */
function getRendererTypeFromURL(): RendererType {
  const params = new URLSearchParams(window.location.search);
  const renderer = params.get("renderer");

  if (renderer === "webgl" || renderer === "css") {
    return renderer;
  }
  return "auto";
}

/**
 * Transform a canvas element to a div for CSS rendering
 * @param canvas The canvas element to transform
 * @returns The new div element
 */
function transformCanvasToDiv(canvas: HTMLCanvasElement): HTMLDivElement {
  const parent = canvas.parentElement!;
  canvas.style.display = "none";
  const div = document.createElement("div");
  div.style.width = "100%";
  div.style.height = "100%";
  div.className = canvas.className;
  parent.appendChild(div);
  return div;
}

export class BlockEngine {
  private renderer: BlockRenderer;
  private container: HTMLElement;
  private canvas: HTMLCanvasElement | null = null;
  private running = false;
  private observer: IntersectionObserver | null = null;
  private isVisible = false;
  private loopRunning = false;
  private rotX = 0.4;
  private rotY = 0;
  private targetRotX = 0.4;
  private targetRotY = 0;
  private zoom = 1;
  private targetZoom = 1;
  private idleMode = true; // Start in idle mode by default
  private lastInputTime = 0;
  private idleTimeoutId: number | null = null;
  private readonly IDLE_DELAY = 2000; // 2 seconds before idle mode
  private readonly IDLE_ROTATION_SPEED = 0.005; // Radians per frame
  private readonly IDLE_FPS = 24; // Target FPS for idle mode
  private readonly IDLE_FRAME_TIME = 1000 / this.IDLE_FPS; // ms per frame (33.33ms)
  private lastFrameTime = 0;

  constructor(
    containerId: string,
    input: BlockModel | Block[],
    modelScale: number = 1.0,
    zoom: number = 0.25,
    rendererType?: RendererType
  ) {
    this.zoom = zoom;
    this.targetZoom = zoom;
    const container = document.getElementById(containerId);
    if (!container)
      throw new Error(`Container with id "${containerId}" not found`);
    this.container = container;

    this.container = container;

    // Determine renderer type from parameter or URL
    const selectedRenderer = rendererType || getRendererTypeFromURL();

    let blocks: Block[];
    if (input instanceof BlockModel) {
      blocks = input.getBlocks();
    } else {
      blocks = input;
    }

    // Select renderer based on type
    switch (selectedRenderer) {
      case "webgl":
        if (container instanceof HTMLCanvasElement) {
          this.canvas = container;
          try {
            this.renderer = new WebGLBlockRenderer(
              this.canvas,
              blocks,
              modelScale
            );
          } catch (e) {
            console.error("WebGL renderer requested but unavailable:", e);
            throw new Error("WebGL renderer requested but not supported");
          }
        } else {
          throw new Error("WebGL renderer requires a canvas element");
        }
        break;

      case "css":
        // If container is a canvas, transform it to a div for CSS rendering
        if (container instanceof HTMLCanvasElement) {
          const div = transformCanvasToDiv(container);
          this.container = div;
          this.canvas = null;
          this.renderer = new CSSBlockRenderer(div, blocks, modelScale);
        } else {
          this.renderer = new CSSBlockRenderer(container, blocks, modelScale);
        }
        console.log("Using CSS 3D renderer as requested");
        break;

      case "auto":
      default:
        // Automatic fallback: Try WebGL first, then CSS
        if (container instanceof HTMLCanvasElement) {
          this.canvas = container;
          try {
            this.renderer = new WebGLBlockRenderer(
              this.canvas,
              blocks,
              modelScale
            );
          } catch (e) {
            console.warn("WebGL unavailable, switching to CSS 3D renderer");
            const div = transformCanvasToDiv(this.canvas);
            this.container = div;
            this.canvas = null;
            this.renderer = new CSSBlockRenderer(div, blocks, modelScale);
          }
        } else {
          // If not a canvas, try CSS 3D first
          this.renderer = new CSSBlockRenderer(container, blocks, modelScale);
        }
        break;
    }

    this.initInput();
  }

  private initInput() {
    const handleInput = (clientX: number, clientY: number) => {
      const element = this.canvas || this.container;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate distance from center of the controller
      const dx = clientX - centerX;
      const dy = clientY - centerY;

      // Scale rotation based on distance (sensitivity)
      const sensitivity = 2.5;
      this.targetRotY = (dx / window.innerWidth) * sensitivity;
      this.targetRotX = 0.4 + (dy / window.innerHeight) * sensitivity;

      // Update last input time and exit idle mode
      this.lastInputTime = Date.now();
      if (this.idleMode) {
        this.idleMode = false;
        this.lastFrameTime = 0; // Reset frame timing for smooth transition
      }

      // Reset idle timeout
      if (this.idleTimeoutId !== null) {
        clearTimeout(this.idleTimeoutId);
      }
      this.idleTimeoutId = window.setTimeout(() => {
        this.idleMode = true;
      }, this.IDLE_DELAY);
    };

    // Mouse events
    document.addEventListener("mousemove", (e) => {
      handleInput(e.clientX, e.clientY);
    });

    // Touch events for mobile support
    document.addEventListener("touchstart", (e) => {
      const touch = e.touches[0];
      handleInput(touch.clientX, touch.clientY);
    }, { passive: true });

    document.addEventListener("touchmove", (e) => {
      const touch = e.touches[0];
      handleInput(touch.clientX, touch.clientY);
    }, { passive: true });

    // document.addEventListener("wheel", (e) => {
    //   const delta = e.deltaY > 0 ? 0.9 : 1.1;
    //   this.targetZoom = Math.max(0.2, Math.min(5.0, this.targetZoom * delta));
    // }, { passive: true });
  }

  /**
   * Setup IntersectionObserver to detect when the renderer is visible
   */
  private setupObserver() {
    if (this.observer) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.isVisible = entry.isIntersecting;

          if (this.isVisible) {
            this.container.style.visibility = "visible";
            if (this.running && !this.loopRunning) {
              this.loop();
            }
          } else {
            this.container.style.visibility = "hidden";
          }
        }
      },
      { threshold: 0 }
    );

    const element = this.canvas || this.container;
    this.observer.observe(element);
  }

  /**
   * Main rendering loop
   */
  private loop() {
    if (!this.running || !this.isVisible) {
      this.loopRunning = false;
      return;
    }

    this.loopRunning = true;

    const now = Date.now();

    if (this.idleMode) {
      // Throttle to 30fps during idle mode
      const timeSinceLastFrame = now - this.lastFrameTime;
      if (timeSinceLastFrame < this.IDLE_FRAME_TIME) {
        // Skip this frame, but keep the loop running
        requestAnimationFrame(() => this.loop());
        return;
      }
      this.lastFrameTime = now;

      // Slow idle rotation - continue from current orientation
      this.rotY += this.IDLE_ROTATION_SPEED;
    } else {
      // Active mode: render every frame at full speed
      // Smooth interpolation for cursor following
      this.rotX += (this.targetRotX - this.rotX) * 0.1;
      this.rotY += (this.targetRotY - this.rotY) * 0.1;
    }
    this.zoom += (this.targetZoom - this.zoom) * 0.1;

    const w = this.canvas ? this.canvas.width : this.container.clientWidth;
    const h = this.canvas ? this.canvas.height : this.container.clientHeight;

    this.renderer.render(w, h, this.rotX, this.rotY, this.zoom);
    requestAnimationFrame(() => this.loop());
  }

  /**
   * Start the rendering loop
   */
  start() {
    this.running = true;
    this.setupObserver();

    // Handle resize for canvas
    if (this.canvas) {
      const resize = () => {
        const dpr = window.devicePixelRatio || 1;
        const rect = this.canvas!.getBoundingClientRect();
        this.canvas!.width = rect.width * dpr;
        this.canvas!.height = rect.height * dpr;
      };
      window.addEventListener("resize", resize);
      resize();
    }
  }

  /**
   * Stop the rendering loop
   */
  stop() {
    this.running = false;
    this.observer?.disconnect();
    this.observer = null;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stop();
    this.renderer.dispose();
  }
}
